/*
 * Opens and closes the fixed reviews drawer.
 *
 * The panel is only ever hidden with CSS, never removed or display:none'd,
 * so the Judge.me widget inside it is rendered once with the rest of the page
 * instead of staying empty until the first open.
 */
class ReviewsTab extends HTMLElement {
  connectedCallback() {
    this.handle = this.querySelector('[data-reviews-tab-open]');
    this.panel = this.querySelector('.reviews-tab__panel');

    this.handle?.addEventListener('click', () => this.open());

    this.querySelectorAll('[data-reviews-tab-close]').forEach((element) => {
      element.addEventListener('click', () => this.close());
    });

    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.close();
      if (event.key === 'Tab') this.trapFocus(event);
    });

    this.watchForReviews();
  }

  /**
   * Judge.me paints a spinner into its container and leaves it there when the
   * store has no reviews yet - and also when the app embed is off, or the
   * script is blocked. None of that is worth showing a shopper, so we watch
   * the container and swap in our own message once it is clear that no
   * reviews are coming.
   */
  watchForReviews() {
    this.widget = this.querySelector('[data-reviews-tab-widget]');
    this.empty = this.querySelector('[data-reviews-tab-empty]');
    if (!this.widget || !this.empty) return;

    const REVIEW_SELECTOR = '.jdgm-rev, .jdgm-carousel__item, [data-review-id]';
    const GIVE_UP_AFTER = 8000;
    const started = Date.now();
    let fetchDone = false;

    const settle = () => {
      // The shop metafield often holds only the first page. Wait for our
      // own request, which loads every published review, before drawing.
      if (fetchDone && this.widget.querySelector(REVIEW_SELECTOR)) {
        window.clearInterval(timer);
        observer.disconnect();
        this.presentReviews();
        return true;
      }

      if (fetchDone && Date.now() - started >= GIVE_UP_AFTER) {
        this.widget.hidden = true;
        this.empty.hidden = false;
        window.clearInterval(timer);
        observer.disconnect();
        return true;
      }

      return false;
    };

    const observer = new MutationObserver(() => settle());
    observer.observe(this.widget, { childList: true, subtree: true });

    // The widget can also render before this element is upgraded, in which
    // case no mutation ever fires - so poll as well.
    const timer = window.setInterval(settle, 500);
    this.fetchFirstPage().finally(() => {
      fetchDone = true;
      settle();
    });
    settle();
  }

  /**
   * Loads every published product review. The shop metafield usually has
   * only the first page, and Judge.me will not fetch the rest inside a drawer.
   */
  async fetchFirstPage() {
    const body = this.widget.querySelector('.jdgm-all-reviews__body');
    if (!body) return;

    const shop = this.widget.dataset.shopDomain;
    if (!shop) return;

    try {
      let page = 1;
      let html = '';
      let total = Infinity;

      while (page <= 20) {
        const url = new URL('https://cdn.judge.me/reviews/all_reviews_js_based');
        url.searchParams.set('platform', 'shopify');
        url.searchParams.set('shop_domain', shop);
        url.searchParams.set('review_type', 'product-reviews');
        url.searchParams.set('page', String(page));

        const response = await fetch(url.href);
        if (!response.ok) break;

        const data = await response.json();
        if (!data.html) break;

        html += data.html;
        if (typeof data.number_of_product_reviews === 'number') {
          total = data.number_of_product_reviews;
        }

        const loaded = html.split('data-review-id').length - 1;
        if (loaded >= total) break;
        page += 1;
      }

      if (!html) return;
      body.innerHTML = html;
    } catch (error) {
      // The empty-state watcher shows the fallback copy.
    }
  }

  /**
   * Judge.me's all-reviews markup includes a histogram, a write-review button
   * and its own chrome. Keep the review text and draw it with the theme.
   */
  presentReviews() {
    if (this.presented) return;

    const nodes = [...this.widget.querySelectorAll('.jdgm-rev')];
    if (!nodes.length) return;
    this.presented = true;

    const list = document.createElement('ol');
    list.className = 'reviews-tab__list';

    nodes.forEach((node) => {
      const score = Number(node.querySelector('.jdgm-rev__rating')?.dataset.score || 0);
      const title = node.querySelector('.jdgm-rev__title')?.textContent.trim() || '';
      const author = node.querySelector('.jdgm-rev__author')?.textContent.trim() || '';
      const productLink = node.querySelector('.jdgm-rev__prod-link');
      const productFull = productLink?.textContent.trim() || '';
      const productUrl = productLink?.getAttribute('href') || '';
      const product = productFull.split('|')[0].trim();
      const rawDate = node.querySelector('.jdgm-rev__timestamp')?.dataset.content || '';
      const bodyEl = node.querySelector('.jdgm-rev__body');
      const paragraphs = [...(bodyEl?.querySelectorAll('p') || [])]
        .map((paragraph) => paragraph.textContent.trim())
        .filter(Boolean);
      const text = paragraphs.length ? paragraphs.join('\n\n') : bodyEl?.textContent.trim() || '';

      const item = document.createElement('li');
      item.className = 'reviews-tab__review';

      const top = document.createElement('div');
      top.className = 'reviews-tab__review-top';

      const stars = document.createElement('span');
      stars.className = 'reviews-tab__stars';
      stars.setAttribute('aria-label', `${score} din 5`);
      stars.innerHTML = this.starMarkup(score);

      const time = document.createElement('time');
      time.className = 'reviews-tab__date';
      const formatted = this.formatDate(rawDate);
      time.textContent = formatted;
      if (rawDate) time.dateTime = rawDate;

      top.append(stars, time);
      item.append(top);

      if (title) {
        const heading = document.createElement('p');
        heading.className = 'reviews-tab__review-title';
        heading.textContent = title;
        item.append(heading);
      }

      if (text) {
        const copy = document.createElement('p');
        copy.className = 'reviews-tab__review-text';
        copy.textContent = text;
        item.append(copy);
      }

      if (author || product) {
        const meta = document.createElement('p');
        meta.className = 'reviews-tab__review-meta';
        if (author) meta.append(author);
        if (author && product) meta.append(' · ');
        if (product) {
          if (productUrl) {
            const link = document.createElement('a');
            link.className = 'reviews-tab__product-link';
            link.href = productUrl;
            link.textContent = product;
            meta.append(link);
          } else {
            meta.append(product);
          }
        }
        item.append(meta);
      }

      list.append(item);
    });

    this.widget.replaceChildren(list);
  }

  starMarkup(score) {
    const filled = Math.max(0, Math.min(5, Math.round(score)));
    return Array.from({ length: 5 }, (_, index) => {
      const state = index < filled ? 'is-on' : 'is-off';
      return `<span class="${state}" aria-hidden="true">★</span>`;
    }).join('');
  }

  formatDate(raw) {
    if (!raw) return '';
    const date = new Date(raw.replace(' UTC', 'Z').replace(' ', 'T'));
    if (Number.isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat('ro-RO', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).format(date);
  }

  get isOpen() {
    return this.classList.contains('is-open');
  }

  open() {
    if (this.isOpen) return;

    this.classList.add('is-open');
    this.handle?.setAttribute('aria-expanded', 'true');
    this.previousOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';

    // Wait for the panel to become visible before moving focus into it.
    window.setTimeout(() => {
      const target = this.querySelector('.reviews-tab__close');
      target?.focus();
    }, 50);
  }

  close() {
    if (!this.isOpen) return;

    this.classList.remove('is-open');
    this.handle?.setAttribute('aria-expanded', 'false');
    document.documentElement.style.overflow = this.previousOverflow || '';
    this.handle?.focus();
  }

  /** Keeps Tab inside the panel while it is open. */
  trapFocus(event) {
    if (!this.isOpen || !this.panel) return;

    const focusable = this.panel.querySelectorAll(
      'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

customElements.define('reviews-tab', ReviewsTab);
